import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Avatar, Chip, Select, MenuItem, IconButton, Button, TextField,
  Paper, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Skeleton, Stack, Divider, CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import api from '../api/axios';
import { getCurrentUser, isAdmin } from '../utils/currentUser';

const ROLE_STYLE = {
  ADMIN: { bg: 'rgba(210,122,90,0.14)', color: '#e39a7f', label: 'Admin' },
  MEMBER: { bg: 'rgba(235,231,225,0.07)', color: '#a19c94', label: 'Member' },
};

function RoleChip({ role }) {
  const s = ROLE_STYLE[role] ?? ROLE_STYLE.MEMBER;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.72rem', height: 22 }}
    />
  );
}

function initials(user) {
  return ((user.nickname || user.email) ?? '?').slice(0, 2).toUpperCase();
}

export default function Team() {
  const qc = useQueryClient();
  const currentUser = getCurrentUser();
  const amAdmin = isAdmin();

  const [newEmail, setNewEmail] = useState('');
  const [wlError, setWlError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  /* ── Queries ─────────────────────────────── */
  const { data: members = [], isLoading: loadingMembers } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get('/team').then((r) => r.data),
  });

  const { data: whitelist = [], isLoading: loadingWL } = useQuery({
    queryKey: ['whitelist'],
    queryFn: () => api.get('/whitelist').then((r) => r.data),
    enabled: amAdmin,
  });

  /* ── Mutations ───────────────────────────── */
  const updateRole = useMutation({
    mutationFn: ({ id, role }) => api.patch(`/team/${id}/role`, { role }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });

  const deleteMember = useMutation({
    mutationFn: (id) => api.delete(`/team/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['team'] }); setConfirmDelete(null); },
  });

  const addWhitelist = useMutation({
    mutationFn: (email) => api.post('/whitelist', { email }).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['whitelist'] }); setNewEmail(''); setWlError(''); },
    onError: (err) => setWlError(err.response?.data?.error || 'Failed to add email'),
  });

  const removeWhitelist = useMutation({
    mutationFn: (id) => api.delete(`/whitelist/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whitelist'] }),
  });

  const avatarInputRef = useRef(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [profileDialog, setProfileDialog] = useState(false);
  const [profileForm, setProfileForm] = useState({ nickname: '', currentPassword: '', newPassword: '', confirmPassword: '' });
  const [profileError, setProfileError] = useState('');

  const saveProfile = useMutation({
    mutationFn: (data) => api.patch('/team/me', data).then((r) => r.data),
    onSuccess: (user) => {
      localStorage.setItem('user', JSON.stringify({ ...currentUser, nickname: user.nickname, avatarUrl: user.avatarUrl }));
      qc.invalidateQueries({ queryKey: ['team'] });
      setProfileDialog(false);
      setProfileError('');
    },
    onError: (err) => setProfileError(err.response?.data?.error || 'Failed to save'),
  });

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarLoading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/upload', fd);
      const user = await api.patch('/team/me', { avatarUrl: data.url }).then(r => r.data);
      localStorage.setItem('user', JSON.stringify({ ...currentUser, avatarUrl: user.avatarUrl }));
      qc.invalidateQueries({ queryKey: ['team'] });
    } finally {
      setAvatarLoading(false);
      e.target.value = '';
    }
  };

  const handleProfileSave = () => {
    if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmPassword) {
      setProfileError('Passwords do not match');
      return;
    }
    const payload = { nickname: profileForm.nickname };
    if (profileForm.newPassword) {
      payload.currentPassword = profileForm.currentPassword;
      payload.newPassword = profileForm.newPassword;
    }
    saveProfile.mutate(payload);
  };

  /* ── Render ──────────────────────────────── */
  return (
    <Box sx={{ maxWidth: 860 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Team</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Manage members, roles and access
        </Typography>
      </Box>

      {/* ── My Profile ──────────────────────── */}
      <Paper sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Your Profile
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          {/* Avatar with upload overlay */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={currentUser?.avatarUrl || undefined}
              sx={{ width: 64, height: 64, fontSize: '1.4rem', bgcolor: 'rgba(235,231,225,0.1)', color: 'text.primary' }}
            >
              {!currentUser?.avatarUrl && initials(currentUser ?? { email: '?' })}
            </Avatar>
            <Tooltip title="Upload avatar">
              <IconButton
                size="small"
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarLoading}
                sx={{
                  position: 'absolute', bottom: -4, right: -4,
                  bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                  width: 24, height: 24,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {avatarLoading
                  ? <CircularProgress size={12} />
                  : <CameraAltIcon sx={{ fontSize: 13 }} />}
              </IconButton>
            </Tooltip>
            <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={handleAvatarUpload} />
          </Box>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={600} noWrap>
              {currentUser?.nickname || currentUser?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{currentUser?.email}</Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <RoleChip role={currentUser?.role ?? 'MEMBER'} />
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                setProfileForm({ nickname: currentUser?.nickname || '', currentPassword: '', newPassword: '', confirmPassword: '' });
                setProfileError('');
                setProfileDialog(true);
              }}
            >
              Edit profile
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* ── Members table ───────────────────── */}
      <Paper sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 3 }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Members ({members.length})
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { color: 'text.secondary', fontSize: '0.74rem', fontWeight: 600 } }}>
              <TableCell>Member</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              {amAdmin && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingMembers
              ? [0, 1, 2].map((i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={amAdmin ? 4 : 3}>
                      <Skeleton variant="text" />
                    </TableCell>
                  </TableRow>
                ))
              : members.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={m.avatarUrl || undefined}
                          sx={{
                            width: 30, height: 30, fontSize: '0.72rem',
                            bgcolor: 'rgba(235,231,225,0.08)', color: 'text.primary',
                          }}
                        >
                          {!m.avatarUrl && initials(m)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={m.id === currentUser?.id ? 600 : 400}>
                            {m.nickname || '—'}
                            {m.id === currentUser?.id && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                (you)
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{m.email}</Typography>
                    </TableCell>
                    <TableCell>
                      {amAdmin && m.id !== currentUser?.id ? (
                        <Select
                          size="small"
                          value={m.role}
                          onChange={(e) => updateRole.mutate({ id: m.id, role: e.target.value })}
                          sx={{ fontSize: '0.78rem', '.MuiSelect-select': { py: 0.5 } }}
                        >
                          <MenuItem value="ADMIN">Admin</MenuItem>
                          <MenuItem value="MEMBER">Member</MenuItem>
                        </Select>
                      ) : (
                        <RoleChip role={m.role} />
                      )}
                    </TableCell>
                    {amAdmin && (
                      <TableCell>
                        {m.id !== currentUser?.id && (
                          <Tooltip title="Remove member">
                            <IconButton size="small" color="error" onClick={() => setConfirmDelete(m)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Whitelist (admin only) ───────────── */}
      {amAdmin && (
        <Paper sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600}>Registration Whitelist</Typography>
            <Typography variant="caption" color="text.secondary">
              Only these emails can create a new account. Empty list = open registration.
            </Typography>
          </Box>

          {/* Add email */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                size="small"
                label="Email address"
                type="email"
                value={newEmail}
                onChange={(e) => { setNewEmail(e.target.value); setWlError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && newEmail) addWhitelist.mutate(newEmail); }}
                sx={{ flex: 1 }}
                error={!!wlError}
                helperText={wlError}
              />
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                disabled={!newEmail}
                onClick={() => addWhitelist.mutate(newEmail)}
                sx={{ flexShrink: 0, alignSelf: 'flex-start' }}
              >
                Add
              </Button>
            </Box>
          </Box>

          {/* List */}
          {loadingWL ? (
            <Box sx={{ p: 2.5 }}>
              <Skeleton variant="text" /><Skeleton variant="text" />
            </Box>
          ) : whitelist.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.disabled', fontSize: '0.84rem' }}>
              Whitelist is empty — anyone can register
            </Box>
          ) : (
            whitelist.map((entry) => (
              <Box
                key={entry.id}
                sx={{
                  px: 2.5, py: 1.25,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  borderBottom: '1px solid', borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                }}
              >
                <Box>
                  <Typography variant="body2">{entry.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Added {new Date(entry.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => removeWhitelist.mutate(entry.id)}>
                    <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))
          )}
        </Paper>
      )}

      {/* Profile edit dialog */}
      <Dialog open={profileDialog} onClose={() => setProfileDialog(false)} maxWidth="xs" fullWidth
>
        <DialogTitle>Edit profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nickname"
              size="small"
              fullWidth
              value={profileForm.nickname}
              onChange={(e) => setProfileForm((f) => ({ ...f, nickname: e.target.value }))}
            />
            <Divider sx={{ my: 0.5 }}><Typography variant="caption" color="text.disabled"><LockOutlinedIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />Change password</Typography></Divider>
            <TextField
              label="Current password"
              size="small"
              fullWidth
              type="password"
              value={profileForm.currentPassword}
              onChange={(e) => setProfileForm((f) => ({ ...f, currentPassword: e.target.value }))}
              autoComplete="current-password"
            />
            <TextField
              label="New password"
              size="small"
              fullWidth
              type="password"
              value={profileForm.newPassword}
              onChange={(e) => setProfileForm((f) => ({ ...f, newPassword: e.target.value }))}
              autoComplete="new-password"
            />
            <TextField
              label="Confirm new password"
              size="small"
              fullWidth
              type="password"
              value={profileForm.confirmPassword}
              onChange={(e) => setProfileForm((f) => ({ ...f, confirmPassword: e.target.value }))}
              autoComplete="new-password"
              error={!!profileError}
              helperText={profileError}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProfileDialog(false)} color="inherit" size="small">Cancel</Button>
          <Button variant="contained" size="small" disabled={saveProfile.isPending} onClick={handleProfileSave}>
            {saveProfile.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Remove Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{confirmDelete?.nickname || confirmDelete?.email}</strong>? This will permanently delete all their tasks, events, and documents.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete(null)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMember.mutate(confirmDelete.id)}
            disabled={deleteMember.isPending}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
